"use client";

import {
  Button,
  CloseButton,
  Dialog,
  Flex,
  Heading,
  Portal,
  Spinner,
  Table,
  Text,
} from "@chakra-ui/react";
import { ColumnHeader, EmptyRow } from "@components/common/TableHelpers";
import EmployeeCreateForm from "@features/back-office/employees/EmployeeCreateForm";
import EmployeeDetailModal from "@features/back-office/employees/EmployeeDetailModal";
import { memo, useCallback, useEffect, useState } from "react";
import { LuPlus } from "react-icons/lu";
import {
  createEmployeeAction,
  getAllEmployeesAction,
} from "server/actions/employee";
import { useOrgId } from "hooks/useOrgId";

interface Employee {
  id: number;
  name: string;
  phoneNumber: string | null;
  email: string | null;
  createdAt: Date;
}

interface EmployeesModalProps {
  open: boolean;
  onClose: () => void;
}

const COLUMN_COUNT = 3;

const EmployeeRow = memo(
  ({
    employee,
    onClick,
  }: {
    employee: Employee;
    onClick: (id: number) => void;
  }) => {
    const onClickRow = useCallback(() => {
      onClick(employee.id);
    }, [onClick, employee.id]);

    return (
      <Table.Row
        fontSize="sm"
        cursor="pointer"
        _hover={{ bg: "bg.subtle" }}
        onClick={onClickRow}
      >
        <Table.Cell border="none" p={3}>
          <Text
            fontSize="sm"
            fontWeight="medium"
            _hover={{ textDecoration: "underline" }}
          >
            {employee.name}
          </Text>
        </Table.Cell>
        <Table.Cell border="none" p={3}>
          <Text fontSize="sm" color="text.secondary">
            {employee.phoneNumber ?? "-"}
          </Text>
        </Table.Cell>
        <Table.Cell
          border="none"
          p={3}
          display={{ base: "none", md: "table-cell" }}
        >
          <Text fontSize="sm" color="text.secondary">
            {employee.email ?? "-"}
          </Text>
        </Table.Cell>
      </Table.Row>
    );
  },
);

const EmployeesModal = ({ open, onClose }: EmployeesModalProps) => {
  const orgId = useOrgId();
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<number | null>(
    null,
  );

  const loadEmployees = useCallback(async () => {
    setLoading(true);
    const result = await getAllEmployeesAction(orgId);
    setEmployees(result);
    setLoading(false);
  }, [orgId]);

  useEffect(() => {
    if (open) {
      loadEmployees();
    }
  }, [open, loadEmployees]);

  const onOpenCreate = useCallback(() => {
    setShowCreateForm(true);
  }, []);

  const onCancelCreate = useCallback(() => {
    setShowCreateForm(false);
  }, []);

  const onSubmitCreate = useCallback(
    async (values: { name: string; phoneNumber: string; email: string }) => {
      await createEmployeeAction(orgId, {
        name: values.name.trim(),
        phoneNumber:
          values.phoneNumber.trim() !== "" ? values.phoneNumber.trim() : null,
        email: values.email.trim() !== "" ? values.email.trim() : null,
      });
      setShowCreateForm(false);
      await loadEmployees();
    },
    [orgId, loadEmployees],
  );

  const onClickEmployee = useCallback((id: number) => {
    setSelectedEmployeeId(id);
  }, []);

  const onCloseDetail = useCallback(() => {
    setSelectedEmployeeId(null);
    loadEmployees();
  }, [loadEmployees]);

  return (
    <>
      <Dialog.Root open={open} onOpenChange={onClose} lazyMount size="lg">
        <Portal>
          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content bgColor="surface.background" p={4}>
              <Flex direction="column" gap={4}>
                <Flex justify="space-between" align="center">
                  <Heading fontSize="lg">My Employees</Heading>
                  <Dialog.CloseTrigger asChild position="static">
                    <CloseButton size="sm" alignSelf="flex-end" />
                  </Dialog.CloseTrigger>
                </Flex>
                {!showCreateForm && (
                  <Button
                    size="2xs"
                    onClick={onOpenCreate}
                    alignSelf="flex-end"
                  >
                    <LuPlus />
                    Add Employee
                  </Button>
                )}
                {showCreateForm && (
                  <EmployeeCreateForm
                    onSubmit={onSubmitCreate}
                    onCancel={onCancelCreate}
                  />
                )}

                {loading && (
                  <Flex justify="center" py={8}>
                    <Spinner size="md" />
                  </Flex>
                )}

                {!loading && (
                  <Table.Root size="sm">
                    <Table.Header>
                      <Table.Row bg="surface.muted">
                          <ColumnHeader>Name</ColumnHeader>
                          <ColumnHeader>Phone</ColumnHeader>
                          <ColumnHeader
                            display={{ base: "none", md: "table-cell" }}
                          >
                            Email
                          </ColumnHeader>
                        </Table.Row>
                      </Table.Header>
                      <Table.Body>
                        {employees.length === 0 && (
                          <EmptyRow colSpan={COLUMN_COUNT}>
                            No employees yet. Add one to get started.
                          </EmptyRow>
                        )}
                        {employees.map((emp) => (
                          <EmployeeRow
                            key={emp.id}
                            employee={emp}
                            onClick={onClickEmployee}
                          />
                        ))}
                      </Table.Body>
                  </Table.Root>
                )}
              </Flex>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>

      <EmployeeDetailModal
        open={selectedEmployeeId != null}
        onClose={onCloseDetail}
        employeeId={selectedEmployeeId}
      />
    </>
  );
};

export default EmployeesModal;
